import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import { uniq } from 'lodash';
import { map, of, switchMap, zip } from 'rxjs';
import { ApiParams } from './api-params';
import { Resource } from './resource';
import { MsGraphApiService } from '@content-platform/web-angular-administration-shared-data-access-ms-graph-api';
import * as _ from 'lodash';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "@content-platform/web-angular-administration-shared-data-access-ms-graph-api";
import * as i3 from "@content-platform/web-angular-shared-util-logging";
import * as i4 from "./api-params";
export class RoleResource extends Resource {
    constructor(http, msGraphApiService, logger, apiParams) {
        super(http, logger.instance('RoleResource'));
        this.msGraphApiService = msGraphApiService;
        this.apiParams = apiParams;
    }
    getUrl() {
        return this.apiParams.getUrl('roles');
    }
    /**
     * Makes sure the users list is a list of strings.
     *
     * @private
     * @param role
     * @returns string of user ids
     */
    getUserIds(role) {
        // We are getting some fake users from backend in the users list for certain roles (v4 uuid check)
        role.users = [
            ...role.users
                .filter((user) => !!user)
                .filter((user) => user.search(/^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i) !== -1)
        ];
        return role.users
            .filter((user) => !!user)
            .map((user) => {
            if (typeof user === 'string') {
                return user;
            }
            return user.objectId;
        });
    }
    /**
     * Getting user profiles from the users in the roles
     *
     * @private
     * @param {Role} role
     * @returns {Observable<UserProfile[]>}
     * @memberof RoleResource
     */
    getUsersProfilesFromRole(role) {
        const users = this.getUserIds(role);
        return users.length > 0 ? this.msGraphApiService.getUsers(users) : of([]);
    }
    /**
     * to get all roles from the database and mapiing user profiles against the users
     *
     * @returns {Observable<Role[]>}
     * @memberof RoleResource
     */
    query() {
        return super.query().pipe(switchMap((roles) => {
            if (roles.length > 0) {
                return this.mapUserProfilesToRoles(roles);
            }
            return of(roles);
        }));
    }
    /**
     * to get all roles from the database and mapiing user profiles against the users
     *
     * @returns {Observable<Role[]>}
     * @memberof RoleResource
     */
    queryById(roleIds) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const queryByIdObs = [];
        roleIds.forEach((id) => {
            queryByIdObs.push(super.get(id));
        });
        return zip(...queryByIdObs).pipe(switchMap((roles) => {
            if (roles.length > 0) {
                const rolesList = _.flatten(roles);
                return this.mapUserProfilesToRoles(rolesList);
            }
            return of(roles);
        }));
    }
    /**
     * getting the single role for an id and mapping the user profile for the user in role.
     *
     * @param {(number | string)} id
     * @returns {Observable<Role>}
     * @memberof RoleResource
     */
    get(id) {
        return super
            .get(id)
            .pipe(switchMap((role) => this.getUsersProfilesFromRole(role).pipe(map((results) => ({ ...role, users: results })))));
    }
    update(role) {
        return super
            .update(role)
            .pipe(switchMap((updatedRole) => this.getUsersProfilesFromRole(updatedRole).pipe(map((results) => ({ ...updatedRole, users: results })))));
    }
    create(role) {
        return super
            .create(role)
            .pipe(switchMap((newRole) => this.getUsersProfilesFromRole(newRole).pipe(map((results) => ({ ...newRole, users: results })))));
    }
    /**
     * Getting user profiles of all the roles, in one request, returns the user entities, as an
     * object. The key is the user id.
     *
     * @private
     * @param {Role} role
     * @returns {Observable<{[id: string]: UserProfile}>}
     * @memberof RoleResource
     */
    getUserProfilesFromRoles(roles) {
        const users = [].concat(...roles.map((role) => this.getUserIds(role)));
        return (users.length > 0 ? this.msGraphApiService.getUsers(uniq(users)) : of([])).pipe(map((userProfiles) => userProfiles.reduce((entities, user) => {
            if (user && user.objectId) {
                return {
                    ...entities,
                    [user.objectId]: user
                };
            }
            return entities;
        }, {})));
    }
    /**
     * Mapping users with their profiles in the roles
     *
     * @private
     * @param {Role[]} roles
     * @returns {Observable<Role[]>}
     * @memberof RoleResource
     */
    mapUserProfilesToRoles(roles) {
        return this.getUserProfilesFromRoles(roles).pipe(map((userEntities) => roles.map((role) => {
            const users = this.getUserIds(role)
                .map((userId) => userEntities[userId])
                .filter((user) => !!user);
            return { ...role, users };
        })));
    }
    /**
     * for logging purpose returns the class name only
     *
     * @readonly
     * @type {string}
     * @memberof RoleResource
     */
    get entityName() {
        return 'Role';
    }
    /**
     * request to convert userprofiles to list of user ids
     *
     * @protected
     * @param {Role} role
     * @returns {Role}
     * @memberof RoleResource
     */
    transformRequest(role) {
        return {
            ...role,
            users: role.users.map((user) => {
                if (typeof user === 'string') {
                    return user;
                }
                return user.objectId;
            })
        };
    }
    static { this.ɵfac = function RoleResource_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || RoleResource)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.MsGraphApiService), i0.ɵɵinject(i3.LoggerService), i0.ɵɵinject(i4.ApiParams)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: RoleResource, factory: RoleResource.ɵfac }); }
}
//# sourceMappingURL=role-resource.js.map